import React, { FunctionComponent } from 'react';
import { concat, mergeDeepWithKey, merge, pathOr, equals } from 'ramda';
import { Query } from 'react-apollo';
import { ApolloError } from 'apollo-boost';

import Loading from 'components/Loading';
import ErrorBoundary from 'components/ErrorBoundary';
import { getGraphQLError } from 'helpers/getGraphQLError';
import { PageDetails } from 'components/Pagination/Pagination';

type Props = {
  query: any;
  variables: any;
  onCompleted: Function;
  onError: Function;
  queryName?: string;
};

type Response = {
  [key: string]: {
    pagination: PageDetails;
    hasNextPage: boolean;
    result: any;
    __typename: string;
  };
};

type ResponseParams = {
  data?: Response;
  loading?: boolean;
  error?: ApolloError;
  fetchMore: any;
};

type PagingInput = {
  page: number;
  pageSize: number;
};

const defaultErrorMessage = {
  displayMessage: 'Something went wrong',
  message: 'Not enough data to display at this time.',
};

const concatValues = (key: string, previosValue: any, currentValue: any) =>
  key === 'result' ? concat(previosValue, currentValue) : currentValue;

const loadMore = (
  fetchMore: Function,
  pagingInput: PagingInput,
  variables: any,
) =>
  fetchMore({
    variables: {
      params: merge(variables.params, { pagingInput }),
    },
    updateQuery: (previousResult: Response, { fetchMoreResult = {} }: any) =>
      mergeDeepWithKey(concatValues, previousResult, fetchMoreResult),
  });

const BulkQuery: FunctionComponent<Props> = ({
  query,
  variables,
  queryName = '',
  onCompleted,
  onError,
}) => {
  let controlData = {};

  return (
    <ErrorBoundary>
      <Query notifyOnNetworkStatusChange query={query} variables={variables}>
        {({ data, loading, error, fetchMore }: ResponseParams) => {
          if (loading) {
            return <Loading loading={loading} />;
          }

          if (error) {
            onError(getGraphQLError(defaultErrorMessage, true, error));
          }

          // TODO: Remove control data once this issue is marked as closed - https://github.com/apollographql/react-apollo/issues/2202
          if (equals(controlData, data)) {
            return null;
          }

          const { hasNextPage, nextPage, pageSize } = pathOr(
            {},
            [queryName, 'pagination'],
            data,
          );

          if (hasNextPage && data) {
            controlData = data;
            loadMore(fetchMore, { pageSize, page: nextPage }, variables);
          } else {
            onCompleted(data);
          }

          return null;
        }}
      </Query>
    </ErrorBoundary>
  );
};

export default BulkQuery;
